import { useCallback } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { zodResolver } from '@hookform/resolvers/zod';
import { MimeType, Task } from '@repo/shared';
import { useRouter } from '@tanstack/react-router';
import { Brain } from 'lucide-react';
import { toast } from 'sonner';

import { useCreateAudioGenerationWithFile } from '@/api/generated';
import type { CreateAudioGenerationWithFileRequest } from '@/api/generated.schemas';
import LanguageCombobox from '@/components/language-combobox';
import ModelsCombobox from '@/components/models-combobox';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardFooter } from '@/components/ui/card';
import { FileInput } from '@/components/ui/file-input';
import FileUpload from '@/components/ui/file-upload';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Spinner } from '@/components/ui/spinner';
import {
  type CreateAudioGenerationWithFileSchema,
  createAudioGenerationWithFileSchema,
} from '@/routes/(app)/(tasks)/lib/generate-audio-schema';

const GenerateAudioWithFile = () => {
  const { t } = useTranslation();
  const router = useRouter();
  const { mutate, isPending } = useCreateAudioGenerationWithFile();
  const form = useForm<CreateAudioGenerationWithFileSchema>({
    defaultValues: {
      files: [],
      models: [],
    },
    resolver: zodResolver(createAudioGenerationWithFileSchema),
  });
  const filesWatch = form.watch('files');

  const handleSubmit = useCallback(
    (values: CreateAudioGenerationWithFileSchema) => {
      const formData = new FormData();
      // looks wrong, but it is correct.
      values.files.forEach((file) => {
        formData.append('files', file);
      });
      formData.append('data', JSON.stringify(values));

      mutate(
        {
          data: formData as unknown as CreateAudioGenerationWithFileRequest,
        },
        {
          onSuccess: async (data) => {
            toast.success(t('clusters.createModelRun.success'));
            await router.navigate({
              to: '/app/clusters',
              search: { datasetId: data.dataset.id, isEval: false },
            });
          },
          onError: () => {
            toast.error(t('clusters.createModelRun.error'));
          },
        }
      );
    },
    [mutate, router, t]
  );

  const handleRemoveFile = useCallback(
    (file: File) => {
      form.setValue(
        'files',
        form.getValues().files.filter((_file) => _file !== file)
      );
    },
    [form]
  );

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(handleSubmit)}>
        <Card>
          <CardContent className='flex flex-col gap-4'>
            <FormField
              control={form.control}
              name='files'
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('datasets.createDataset.file')}</FormLabel>
                  <FormControl>
                    <FileInput
                      {...field}
                      mimeTypes={[
                        MimeType.XML_APPLICATION,
                        MimeType.XML_TEXT,
                        MimeType.DITA,
                        MimeType.TXT,
                      ]}
                      multiple
                    />
                  </FormControl>
                </FormItem>
              )}
            />
            {filesWatch.map((file) => (
              <FileUpload
                key={file.name}
                id={file.name}
                name={file.name}
                size={file.size}
                onRemove={() => {
                  handleRemoveFile(file);
                }}
              />
            ))}
            <FormField
              control={form.control}
              name='models'
              render={({ field }) => (
                <FormItem>
                  <FormLabel>
                    {t('tasks.extractStructure.form.models.label')}
                  </FormLabel>

                  <FormControl>
                    <ModelsCombobox
                      {...field}
                      task={Task.GENERATE_AUDIO}
                      values={field.value}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name='targetLanguageCode'
              render={({ field }) => (
                <FormItem>
                  <FormLabel>
                    {t('tasks.generateAudio.form.targetLanguageCode.label')}
                  </FormLabel>
                  <FormControl>
                    <LanguageCombobox
                      {...field}
                      placeholder={t(
                        'tasks.generateAudio.form.targetLanguageCode.placeholder'
                      )}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </CardContent>
          <CardFooter>
            <Button
              type='submit'
              disabled={isPending}>
              <Button.Icon side={'left'}>
                {isPending ? <Spinner className='text-inherit' /> : <Brain />}
              </Button.Icon>
              {t('tasks.generateAudio.form.submit')}
            </Button>
          </CardFooter>
        </Card>
      </form>
    </Form>
  );
};

export default GenerateAudioWithFile;
